import * as React from 'react';
import styled, { css } from 'styled-components';

import { Slot } from '../../internals/Slot';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { mediaQuery } from '../../tokens/breakpoints';
import { rem } from '../../tokens/spacing';
export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'body'
  | 'body-field'
  | 'body-s'
  | 'body-xs'
  | 'body-xxs';

const HEADING_TAGS: TypographyVariant[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'];

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: TypographyVariant;
  as?: React.ElementType;
  color?: string;
  fontWeight?: 400 | 500 | 600 | 700;
  weight?: number | string;
  align?: 'left' | 'center' | 'right';
  truncate?: boolean;
  /** Scale down headings on mobile viewports. Defaults to false (no change). */
  responsive?: boolean;
  asChild?: boolean;
  semanticId?: string;
  children?: React.ReactNode;
}

const variantStyles = (variant: TypographyVariant) => {
  switch (variant) {
    case 'h1':
      return css`
        font-size: ${rem(36)};
        line-height: ${rem(40)};
      `;
    case 'h2':
      return css`
        font-size: ${rem(32)};
        line-height: ${rem(38)};
      `;
    case 'h3':
      return css`
        font-size: ${rem(26)};
        line-height: ${rem(30)};
      `;
    case 'h4':
      return css`
        font-size: ${rem(22)};
        line-height: ${rem(25)};
      `;
    case 'h5':
      return css`
        font-size: ${rem(20)};
        line-height: ${rem(24)};
      `;
    case 'h6':
      return css`
        font-size: ${rem(16)};
        line-height: ${rem(18)};
      `;
    case 'p':
      return css`
        font-size: ${rem(36)};
        line-height: ${rem(40)};
      `;
    case 'body':
      return css`
        font-size: ${rem(16)};
        line-height: ${rem(22)};
      `;
    case 'body-field':
      return css`
        font-size: ${rem(16)};
        line-height: ${rem(16)};
      `;
    case 'body-s':
      return css`
        font-size: ${rem(14)};
        line-height: ${rem(18)};
      `;
    case 'body-xs':
      return css`
        font-size: ${rem(12)};
        line-height: ${rem(14)};
      `;
    case 'body-xxs':
      return css`
        font-size: ${rem(10)};
        line-height: ${rem(12)};
      `;
    default:
      return css``;
  }
};

const responsiveHeadingStyles = (variant: TypographyVariant) => {
  switch (variant) {
    case 'h1':
      return css`
        @media (max-width: 768px) {
          font-size: ${rem(28)};
          line-height: ${rem(34)};
        }
      `;
    case 'h2':
      return css`
        @media (max-width: 768px) {
          font-size: ${rem(26)};
          line-height: ${rem(32)};
        }
      `;
    case 'h3':
      return css`
        @media (max-width: 768px) {
          font-size: ${rem(22)};
          line-height: ${rem(26)};
        }
      `;
    case 'h4':
      return css`
        @media (max-width: 768px) {
          font-size: ${rem(18)};
          line-height: ${rem(22)};
        }
      `;
    default:
      return css``;
  }
};

const Root = styled.span.withConfig({
  shouldForwardProp: (prop) =>
    !['$variant', '$truncate', '$align', '$weight', '$color', '$responsive'].includes(String(prop)),
})<{
  $variant: TypographyVariant;
  $truncate: boolean;
  $align?: 'left' | 'center' | 'right';
  $weight?: number | string;
  $color?: string;
  $responsive?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0;
  color: ${({ $color }) => $color ?? 'var(--ui-text)'};
  font-weight: ${({ $weight }) => $weight ?? 400};

  ${({ $align }) =>
    $align &&
    css`
      text-align: ${$align};
    `}

  ${({ $variant }) => variantStyles($variant)}

  ${({ $responsive, $variant }) => $responsive && responsiveHeadingStyles($variant)}

  ${({ $truncate }) =>
    $truncate &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
`;

export const Typography = React.forwardRef<HTMLElement, TypographyProps>(function Typography(
  {
    variant = 'body',
    as,
    color,
    fontWeight,
    weight,
    align,
    truncate = false,
    responsive = false,
    asChild = false,
    semanticId,
    children,
    ...rest
  },
  ref,
) {
  const textTag = HEADING_TAGS.includes(variant) ? variant : 'span';
  const Component = as ?? textTag;
  const semanticProps = getSemanticAttributes(semanticId);
  const resolvedWeight = fontWeight ?? weight;

  if (asChild) {
    return (
      <Root
        $variant={variant}
        $truncate={truncate}
        $align={align}
        $weight={resolvedWeight}
        $color={color}
        $responsive={responsive}
        as="span"
        style={{ display: 'contents' }}
      >
        <Slot ref={ref as React.ForwardedRef<HTMLElement>} {...semanticProps} {...rest}>
          {children}
        </Slot>
      </Root>
    );
  }

  return (
    <Root
      ref={ref as React.ForwardedRef<HTMLSpanElement>}
      as={Component}
      $variant={variant}
      $truncate={truncate}
      $align={align}
      $weight={resolvedWeight}
      $color={color}
      $responsive={responsive}
      {...semanticProps}
      {...rest}
    >
      {children}
    </Root>
  );
});

Typography.displayName = 'Typography';
